/**
 * Cometa Taller · Capa Cinematográfica de Animación
 *
 * CONVENCIÓN DE ENGANCHE (Sección 8.2 del Brief):
 * - Los efectos se enganchan exclusivamente por atributos data, jamás por clases de estilo:
 *     data-ct-anim="nombre-efecto"   -> Nombre del efecto del catálogo (Sección 8.3)
 *     data-ct-anim-delay="0.1"       -> Retardo en segundos
 *     data-ct-anim-stagger="0.06"    -> Cascada entre hijos si el efecto lo admite
 *     data-ct-anim-off               -> Apaga el efecto en el elemento sin modificar marcado
 *
 * REGLA DE PRECEDENCIA Y ROBUSTEZ (Sección 8.1):
 * - La página DEBE verse completa y correcta con JavaScript desactivado.
 * - Los estados iniciales ocultos o desplazados SOLO existen cuando document.documentElement
 *   tiene la clase .ct-js (inyectada inline en el <head> antes de cualquier render).
 *
 * REGLA 8.5 (CRÍTICA):
 * - NUNCA animar directamente con GSAP un elemento que ya lleve transform: skewY() o rotate() en CSS.
 * - Siempre se anima el contenedor envolvente exterior.
 *
 * REGLA 8.6 (ACCESIBILIDAD Y PRESUPUESTO):
 * - Respeta prefers-reduced-motion con gsap.matchMedia(). En la rama reducida NO se registra ningún efecto.
 * - Solo se animan transform y opacity ligados a scroll.
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Registro de plugins oficiales de GSAP
gsap.registerPlugin(ScrollTrigger);

/**
 * Catálogo central de efectos (Sección 8.3)
 * En la Etapa 1 el registro nace preparado y vacío de animaciones activas,
 * listo para que en la Etapa 9 se implemente cada función del catálogo.
 *
 * Vocabulario cerrado:
 * - reveal-texto
 * - reveal-bloque (defecto)
 * - reveal-grilla
 * - parallax-producto
 * - banda-entra
 * - cifras
 * - pin-escena
 * - dibujo-cometa
 * - marquesina-scroll
 * - cortina
 */
export const catalogoEfectos = {
  'reveal-bloque': (el, { delay = 0 } = {}) => {
    gsap.from(el, {
      opacity: 0,
      y: 28,
      duration: 0.7,
      delay,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: el,
        start: 'top 92%',
        once: true
      }
    });
  },
  'reveal-grilla': (el, { delay = 0, stagger = 0.06 } = {}) => {
    const targets = el.classList.contains('ct-catalogo-item') ? el : (el.children.length ? el.children : el);
    gsap.from(targets, {
      opacity: 0,
      y: 28,
      duration: 0.6,
      delay,
      stagger,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: el,
        start: 'top 95%',
        once: true
      }
    });
  },
  'banda-entra': (el, { delay = 0 } = {}) => {
    gsap.from(el, {
      opacity: 0,
      duration: 0.8,
      delay,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: el,
        start: 'top 92%',
        once: true
      }
    });
  },
  'parallax-producto': (el) => {
    gsap.to(el, {
      y: -24,
      ease: 'none',
      scrollTrigger: {
        trigger: el,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true
      }
    });
  }
};

/**
 * Inicializador principal de la capa de animación
 */
export function initAnimaciones() {
  // Verificación de soporte y entorno
  if (typeof window === 'undefined') return;

  // Configuración de accesibilidad mediante gsap.matchMedia()
  const mm = gsap.matchMedia();

  // 1. RAMA PREFERS-REDUCED-MOTION: REDUCE
  // "En la rama de movimiento reducido NO se registra ningún efecto y todo queda en su estado final.
  // No basta con poner duration: 0: el pin y el scrub siguen secuestrando el scroll." (Sección 8.6)
  mm.add('(prefers-reduced-motion: reduce)', () => {
    // Intencionalmente vacío: ningún ScrollTrigger ni animación se registra.
    // Todos los elementos conservan su estado natural de maquetación CSS.
  });

  // 2. RAMA ESTÁNDAR (CON MOVIMIENTO PERMITIDO)
  mm.add('(prefers-reduced-motion: no-preference)', async (context) => {
    const elementosAnimables = document.querySelectorAll('[data-ct-anim]:not([data-ct-anim-off])');
    if (!elementosAnimables.length) return;

    // Carga bajo demanda de plugins pesados según presencia real en el DOM (Regla 8.6 y corrección 3b)
    const tieneRevealTexto = document.querySelector('[data-ct-anim="reveal-texto"]:not([data-ct-anim-off])');
    const tieneDibujoCometa = document.querySelector('[data-ct-anim="dibujo-cometa"]:not([data-ct-anim-off])');

    if (tieneRevealTexto) {
      // SplitText o lógica de splitting modular cargada dinámicamente
      try {
        // dynamic import chunk
        // const { SplitText } = await import('gsap/SplitText');
        // gsap.registerPlugin(SplitText);
      } catch (e) {
        console.warn('SplitText no disponible o cargado condicionalmente', e);
      }
    }

    if (tieneDibujoCometa) {
      // DrawSVG cargado dinámicamente sólo si existe dibujo-cometa
      try {
        // const { DrawSVGPlugin } = await import('gsap/DrawSVGPlugin');
        // gsap.registerPlugin(DrawSVGPlugin);
      } catch (e) {
        console.warn('DrawSVG no disponible o cargado condicionalmente', e);
      }
    }

    elementosAnimables.forEach((el) => {
      const nombreEfecto = el.getAttribute('data-ct-anim');
      if (nombreEfecto && typeof catalogoEfectos[nombreEfecto] === 'function') {
        const delay = parseFloat(el.getAttribute('data-ct-anim-delay')) || 0;
        const stagger = parseFloat(el.getAttribute('data-ct-anim-stagger')) || 0.06;
        catalogoEfectos[nombreEfecto](el, { delay, stagger });
      }
    });

    // Refresco seguro de ScrollTrigger tras carga de imágenes
    window.addEventListener('load', () => {
      ScrollTrigger.refresh();
    });
  });

  return mm;
}

// Auto-inicialización como módulo ES
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAnimaciones());
  } else {
    initAnimaciones();
  }
}
